import { Vector2 } from "../../../common/vector2.js";
import { formatShortDuration } from "../../../common/common_funcs.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ImagesHolder } from "../../../image_loader/images_holder.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { TransformRotate } from "../../../transforms/transform_rotate.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UICanvasRect } from "../../common_ui_elements/ui_canvas_rect.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MirrorMode, UIMirrorElement } from "../../common_ui_elements/ui_mirror_element.js";
import { UIRimLightGlow } from "../../common_ui_elements/ui_rim_light_glow.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { UIWheelSegments } from "../../common_ui_elements/ui_wheel_segments.js";
import { TutorialCursor } from "../../common_ui_elements/tutorial_cursor.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";
import { RewardWidgetsService } from "../../../reward_widgets/reward_widgets_service.js";
import { ApiError } from "../../../api/http_client.js";
import { LocalizationManager } from "../../../localization/localization_manager.js";
import { OverlayAppear } from "../overlay_appear.js";
import { RewardFlyToHud } from "../../reward_fly/reward_fly_to_hud.js";
import { RewardFlySources } from "../../reward_fly/reward_fly_sources.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import type { DailyTaskRewardDto } from "../../../api/dto/daily_tasks.dto.js";

const WHEEL_SIZE = UIWheelSegments.DESIGN_SIZE;
const BUTTON_OFFSET_Y = 733;

const SEGMENT_COUNT = UIWheelSegments.SEGMENT_COUNT;
const SEGMENT_ANGLE = UIWheelSegments.SEGMENT_ANGLE;

const SPIN_MS = 5000;
const SPIN_FULL_TURNS = 6;
const IDLE_TWINKLE_MS = 900;
const REWARD_COIN_SIZE = 170;

const APPEAR_DELAY_MS = 20;
const APPEAR_WHEEL_FROM_SCALE = 0.6;
const APPEAR_WHEEL_OVERSHOOT = 1.04;
const APPEAR_POINTER_FROM_SCALE = 0.4;
const APPEAR_TITLE_DROP = -120;
const APPEAR_CLOSE_FROM_SCALE = 0.6;

export class WheelOfFortuneOverlay extends AbstractOverlay
{
  private segments: UIWheelSegments | null = null;
  private title: UIImage | null = null;
  private wheel: UIElementHolder | null = null;
  private spin_button: UIImage | null = null;
  private pointer: UIImage | null = null;
  private bulbs: ( UIImage | null )[] = [];
  private glows: ( UIRimLightGlow | null )[] = [];

  private close_button: UIImage | null = null;
  private result_dim: UICanvasRect | null = null;
  private spin_text: UITextElement | null = null;
  private reward_popup: UIElementHolder | null = null;
  private reward_icon: UIImage | null = null;
  private reward_text: UITextElement | null = null;
  private claim_button: UIImage | null = null;
  private spin_cursor: TutorialCursor | null = null;

  private cape: UIImage | null = null;
  private refresh_pill: RoundedRectangle | null = null;
  private refresh_text: UITextElement | null = null;
  private refresh_text_value = "";
  private refresh_at = 0;

  private spins_used = 0;
  private spins_total = 3;
  private pending_reward: { reward: DailyTaskRewardDto | null; spins_used: number } | null = null;
  private is_exhausted = false;

  private is_spinning = false;
  private is_result_shown = false;
  private last_update = 0;
  private last_angle = 0;
  private chase_head = 0;
  private max_angular_speed = 1;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: any,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );

    const wheel_state = RewardWidgetsService.wheel();
    this.spins_used = wheel_state?.spins_used ?? 0;
    this.spins_total = wheel_state?.spins_total ?? this.spins_total;

    this.createCloseButton();
    this.createWheel();
    this.createSpinText();
    this.createSpinButton();
    this.createRefreshTimer();
    this.createResultDim();
    this.createTitle();
    this.createPointer();
    this.createRewardPopup();
    this.createClaimButton();

    this.startIdleLights( performance.now() );

    if ( params?.rewarded === false )
      this.playExhaustedAppear();
    else
      this.createSpinCursor();
  }

  private createSpinCursor(): void
  {
    this.spin_cursor = new TutorialCursor( this.ctx );
    this.addChild( this.spin_cursor );
    this.pointAtSpinButton();
  }

  private pointAtSpinButton(): void
  {
    const button = this.spin_button;
    if ( !button || !this.spin_cursor )
      return;

    const pos = button.pixelPosImportant();
    const tip_x = pos.x + button.getRect().getW2();
    const tip_y = pos.y + button.getRect().getH2();
    this.spin_cursor.pointAt( new Vector2( tip_x, tip_y ) );
  }

  private playExhaustedAppear(): void
  {
    const start = performance.now() + APPEAR_DELAY_MS;

    this.spin_button?.setClickable( false );
    this.spin_button?.setOpacityImmediate( 0 );

    OverlayAppear.pop( this.wheel, start, { from_scale: APPEAR_WHEEL_FROM_SCALE, overshoot: APPEAR_WHEEL_OVERSHOOT } );
    OverlayAppear.rise( this.title, start + 80, APPEAR_TITLE_DROP );
    OverlayAppear.pop( this.pointer, start + 200, { from_scale: APPEAR_POINTER_FROM_SCALE } );
    OverlayAppear.rise( this.spin_text, start + 240 );
    this.showExhausted( start + 280 );
    OverlayAppear.pop( this.close_button, start + 420, { from_scale: APPEAR_CLOSE_FROM_SCALE } );
  }

  private createTitle(): void
  {
    const w = LayoutScaler.widthFromDesign( 600 );
    const h = LayoutScaler.heightFromDesign( 300 );
    const x = this.rect.getW2() - w / 2;
    const y = this.yFromWheel( -707 ) - h / 2;

    const title = new UIImage( this.ctx, x, y, w, h, ImgID.UI_WheelOfFortuneTitle );

    title.setClickable( false );
    this.addChild( title );
    this.title = title;
  }

  private createCloseButton(): void
  {
    const size = LayoutScaler.widthFromDesign( 120 );
    const x = this.rect.getW() - LayoutScaler.xFromDesign( 131 ) - size / 2;
    const y = this.yFromWheel( -716 ) - size / 2;

    const close = new UIImage( this.ctx, x, y, size, size, ImgID.UI_SettingsDialogCloseBtn, undefined, true );

    close.setOnClick( () =>
    {
      if ( this.is_spinning ) return;
      OverlayManager.closeOverlay();
    } );

    this.close_button = close;
    this.addChild( close );
  }

  private createWheel(): void
  {
    const size = LayoutScaler.widthFromDesign( WHEEL_SIZE );
    const x = this.rect.getW2() - size / 2;
    const y = this.yFromWheel( 0 ) - size / 2;

    const wheel = new UIElementHolder( this.ctx, x, y, size, size, false );
    wheel.setClickable( false );

    const bg = new UIMirrorElement( this.ctx, 0, 0, size, size, MirrorMode.horizontal );
    bg.setImageSource( ImgID.UI_WheelOfFortuneBg );
    wheel.addChild( bg );

    this.segments = new UIWheelSegments( this.ctx, 0, 0, size, this.segmentRewards() );
    wheel.addChild( this.segments );

    const center_size = LayoutScaler.widthFromDesign( 360 );
    const center_x = ( size - center_size ) / 2;
    const center_y = ( size - center_size ) / 2 + LayoutScaler.heightFromDesign( 10 );

    const center = new UIImage( this.ctx, center_x, center_y, center_size, center_size, ImgID.UI_WheelOfFortuneCenter );
    wheel.addChild( center );

    this.createRimLights( wheel, size );
    this.createCape( wheel, size );

    wheel.getChildren().forEach( child => child.setClickable( false ) );
    this.addChild( wheel );
    this.wheel = wheel;
  }

  private createRimLights( wheel: UIElementHolder, size: number ): void
  {
    const r = LayoutScaler.widthFromDesign( 430 );

    this.bulbs = [null];
    this.glows = [null];

    for ( let i = 1; i < SEGMENT_COUNT; i++ )
    {
      const is_purple = i % 2 === 1;
      const dot_size = LayoutScaler.widthFromDesign( is_purple ? 54 : 64 );
      const angle = i * SEGMENT_ANGLE;
      const x = size / 2 + Math.sin( angle ) * r - dot_size / 2;
      const y = size / 2 - Math.cos( angle ) * r - dot_size / 2;
      const img_id = is_purple ? ImgID.UI_WheelOfFortuneShinePurple : ImgID.UI_WheelOfFortuneShineWhite;
      const halo_rgb = is_purple ? "255, 120, 255" : "255, 250, 210";

      const bulb = new UIImage( this.ctx, x, y, dot_size, dot_size, img_id );
      const glow = new UIRimLightGlow( this.ctx, x, y, dot_size, img_id, halo_rgb );
      glow.setOpacityImmediate( 0 );

      this.bulbs.push( bulb );
      this.glows.push( glow );
      wheel.addChild( bulb );
      wheel.addChild( glow );
    }
  }

  private createCape( wheel: UIElementHolder, size: number ): void
  {
    const w = LayoutScaler.widthFromDesign( 995 );
    const h = LayoutScaler.heightFromDesign( 794 );
    const x = ( size - w ) / 2;
    const y = LayoutScaler.heightFromDesign( -25 );

    this.cape = new UIImage( this.ctx, x, y, w, h, ImgID.UI_WheelOfFortuneCape );
    this.cape.setClickable( false );
    this.cape.setOpacityImmediate( 0 );
    wheel.addChild( this.cape );
  }

  private createPointer(): void
  {
    const center = this.wheelCenter();
    const w = LayoutScaler.widthFromDesign( 70 );
    const h = LayoutScaler.heightFromDesign( 109 );
    const x = center.x - w / 2;
    const y = center.y - LayoutScaler.heightFromDesign( 378 ) - h;

    this.pointer = new UIImage( this.ctx, x, y, w, h, ImgID.UI_WheelOfFortunePointer );
    this.pointer.enableRotation();
    this.pointer.setClickable( false );
    this.addChild( this.pointer );
  }

  private createResultDim(): void
  {
    const w = this.rect.getW();
    const h = this.rect.getH();

    this.result_dim = new UICanvasRect( this.ctx, 0, 0, w, h, "rgba(0, 0, 0, 0.7)" );
    this.result_dim.setClickable( false );
    this.result_dim.setOpacityImmediate( 0 );
    this.addChild( this.result_dim );
  }

  private wheelCenter(): Vector2
  {
    return new Vector2( this.rect.getW2(), this.rect.getH2() );
  }

  private yFromWheel( design_offset: number ): number
  {
    return this.rect.getH2() + LayoutScaler.heightFromDesign( design_offset );
  }

  private createSpinText(): void
  {
    const w = this.rect.getW();
    const h = LayoutScaler.heightFromDesign( 80 );
    const y = this.yFromWheel( 504 ) - h / 2;

    const text = new UITextElement(
      this.ctx,
      0,
      y,
      w,
      h,
      {
        text: this.spinText(),
        color_map: { speen: "#BBC3BA" },
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    text.setClickable( false );
    this.spin_text = text;
    this.addChild( text );
  }

  private spinText(): string
  {
    return `<speen>${LocalizationManager.t( "WHEEL.SPIN" )}</speen> ${this.spins_total - this.spins_used}/${this.spins_total}`;
  }

  private createSpinButton(): void
  {
    const size = LayoutScaler.widthFromDesign( 324 );
    const x = this.rect.getW2() - size / 2;
    const y = this.yFromWheel( BUTTON_OFFSET_Y ) - size / 2;

    this.spin_button = new UIImage( this.ctx, x, y, size, size, ImgID.UI_WheelOfFortuneButtonActive, {}, true );
    this.spin_button.setOnClick( () => this.startSpin() );
    this.addChild( this.spin_button );
  }

  private segmentRewards(): DailyTaskRewardDto[]
  {
    const segments = RewardWidgetsService.wheel()?.segments ?? [];
    return Array.from( { length: SEGMENT_COUNT }, ( _, i ) => segments[i] ?? { type: "coin", amount: 0 } );
  }

  private async startSpin(): Promise<void>
  {
    if ( this.is_spinning || this.is_result_shown || this.is_exhausted || !this.segments ) return;

    this.is_spinning = true;
    this.spin_button?.setClickable( false );
    this.spin_cursor?.hide();

    let target: number;
    try
    {
      const result = await RewardWidgetsService.spinWheel();
      target = result.segment_index;
      this.pending_reward = { reward: result.rewards[0] ?? null, spins_used: result.spins_used };
    }
    catch ( e )
    {
      console.warn( "[Wheel] Spin failed", e );
      this.is_spinning = false;
      if ( e instanceof ApiError && e.status === 409 )
      {
        await RewardWidgetsService.refresh();
        this.showExhausted( performance.now() );
        return;
      }
      this.spin_button?.setClickable( true );
      this.pointAtSpinButton();
      return;
    }

    this.animateSpinTo( target );
  }

  private animateSpinTo( target: number ): void
  {
    if ( !this.segments ) return;

    const segments = this.segments;

    const windup_ms = 300;
    const settle_ms = 450;

    const turn = Math.PI * 2;
    const from = ( ( segments.getRotationImportant() % turn ) + turn ) % turn;
    const target_mod = -( target + 0.5 ) * SEGMENT_ANGLE;
    const forward = ( ( target_mod - from ) % turn + turn ) % turn;

    const windup_angle = from - 0.2;
    const target_angle = from + forward + SPIN_FULL_TURNS * turn;
    const overshoot_angle = target_angle + 0.06;

    segments.setRotationImmediate( from );
    this.last_angle = from;
    this.is_spinning = true;
    this.max_angular_speed = 3 * ( overshoot_angle - windup_angle ) / SPIN_MS;

    const now = performance.now();
    const spin_start = now + windup_ms;
    const settle_start = spin_start + SPIN_MS;

    segments.addTransform( new TransformRotate(
      segments, true, from, windup_angle, now, windup_ms, { easing: Easing.EaseInOutQuad }
    ) );

    segments.addTransform( new TransformRotate(
      segments, true, windup_angle, overshoot_angle, spin_start, SPIN_MS, { easing: Easing.EaseOutPow, easing_pow: 3 }
    ) );

    const settle = new TransformRotate(
      segments, true, overshoot_angle, target_angle, settle_start, settle_ms, { easing: Easing.EaseInOutQuad }
    );
    settle.setOnFinished( () => this.finishSpin( target_angle, target ) );
    segments.addTransform( settle );

    this.spin_button?.setImageId( ImgID.UI_WheelOfFortuneButtonUnactive );
    this.startChaseLights( now );
  }

  private finishSpin( target_angle: number, target_segment: number ): void
  {
    this.segments?.setRotationImmediate( target_angle % ( Math.PI * 2 ) );
    this.last_angle = this.segments?.getRotationImportant() ?? 0;
    this.is_spinning = false;

    const now = performance.now();
    this.flashLights( now );
    void target_segment;
    this.showResult( this.pending_reward?.reward ?? null, now + 250 );
  }

  private showResult( reward: DailyTaskRewardDto | null, start_time: number ): void
  {
    const dim_ms = 300;

    this.is_result_shown = true;

    this.result_dim?.addTransform( new TransformOpacity(
      this.result_dim, true, 0, 1, start_time, dim_ms, { easing: Easing.EaseOutQuad }
    ) );

    for ( const el of [this.spin_button, this.close_button] )
    {
      if ( !el ) continue;
      el.setClickable( false );
      el.fadeOut( dim_ms, start_time, Easing.EaseOutQuad );
    }

    this.showRewardPopup( reward, start_time );
    this.showClaimButton( start_time + 350 );
  }

  private claim(): void
  {
    if ( !this.is_result_shown ) return;

    const hide_ms = 250;
    const now = performance.now();

    const stats_before = StatisticsStorage.load();
    const reward_source = this.rewardFlySource( this.pending_reward?.reward ?? null );

    RewardWidgetsService.applyClaimedRewards();
    this.spins_used = this.pending_reward?.spins_used ?? this.spins_used + 1;
    this.pending_reward = null;

    if ( reward_source )
      RewardFlyToHud.play( this.ctx, this, [ reward_source ], stats_before );

    this.claim_button?.setClickable( false );
    this.claim_button?.stopAllTransforms();
    this.claim_button?.fadeOut( hide_ms, now, Easing.EaseOutQuad );
    this.reward_popup?.fadeOut( hide_ms, now, Easing.EaseOutQuad );
    this.result_dim?.fadeOut( hide_ms, now, Easing.EaseOutQuad );

    this.close_button?.fadeIn( hide_ms, now, Easing.EaseOutQuad );
    this.close_button?.setClickable( true );

    this.is_result_shown = false;

    if ( this.spins_used >= this.spins_total )
    {
      this.showExhausted( now );
      return;
    }

    this.spin_text?.setText( this.spinText() );
    this.spin_button?.setImageId( ImgID.UI_WheelOfFortuneButtonActive );
    this.spin_button?.fadeIn( hide_ms, now, Easing.EaseOutQuad );
    this.spin_button?.setClickable( true );
  }

  private rewardFlySource( reward: DailyTaskRewardDto | null )
  {
    const popup = this.reward_popup;
    if ( !popup || !reward || reward.amount <= 0 || UIWheelSegments.rewardIcon( reward ) === null )
      return null;

    const { w } = this.rewardPopupSize();
    const coin_size = LayoutScaler.widthFromDesign( REWARD_COIN_SIZE );
    const position = popup.getAbsolutePosition();
    const center = new Vector2( position.x + w / 2, position.y + coin_size / 2 );

    return RewardFlySources.fromReward( reward, center, coin_size );
  }

  private showExhausted( now: number, animate: boolean = true ): void
  {
    this.is_exhausted = true;
    this.spins_used = this.spins_total;
    this.refresh_at = RewardWidgetsService.nextMs( RewardWidgetsService.wheel()?.next_available_at ?? null );

    this.spin_text?.setText( LocalizationManager.t( "WHEEL.NEXT_PRIZES_IN" ) );
    this.updateRefreshTimer();

    if ( !animate )
    {
      this.spin_button?.setClickable( false );
      this.spin_button?.setOpacityImmediate( 0 );
      this.refresh_pill?.setOpacityImmediate( 1 );
      this.cape?.setOpacityImmediate( 1 );
      return;
    }

    const drop_ms = 420;
    const squash_ms = 90;

    this.refresh_pill?.fadeIn( drop_ms, now + drop_ms * 0.5, Easing.EaseOutQuad );

    const cape = this.cape;
    if ( !cape ) return;

    const final_pos = cape.pixelPosImportant().clone();
    const start_pos = final_pos.clone();
    start_pos.y -= LayoutScaler.heightFromDesign( 250 );

    const land_time = now + drop_ms;
    const normal = new Vector2( 1, 1 );
    const squash = new Vector2( 1.04, 0.94 );

    cape.setPixelPos( start_pos, true );
    cape.addTransform( new TransformOpacity( cape, true, 0, 1, now, drop_ms * 0.6, { easing: Easing.EaseOutQuad } ) );
    cape.addTransform( new TransformStraightMove( cape, true, start_pos, final_pos, now, drop_ms, { easing: Easing.EaseInQuad } ) );
    cape.addTransform( new TransformScale( cape, true, normal, squash, land_time, squash_ms, { easing: Easing.EaseOutQuad } ) );
    cape.addTransform( new TransformScale( cape, true, squash, normal, land_time + squash_ms, squash_ms * 2, { easing: Easing.EaseInOutQuad } ) );
  }

  private updateRefreshTimer(): void
  {
    if ( !this.is_exhausted || !this.refresh_text ) return;

    const text = formatShortDuration( this.refresh_at - Date.now() );
    if ( text === this.refresh_text_value ) return;

    this.refresh_text_value = text;
    this.refresh_text.setText( text );
  }

  private createRefreshTimer(): void
  {
    const w = LayoutScaler.widthFromDesign( 300 );
    const h = LayoutScaler.heightFromDesign( 70 );
    const x = this.rect.getW2() - w / 2;
    const y = this.yFromWheel( 591 ) - h / 2;

    const pill = new RoundedRectangle(
      this.ctx,
      x,
      y,
      w,
      h,
      LayoutScaler.cornerRadiusFromDesign( 35 ),
      ColorScheme.hearts_timer_bg,
      {},
      false,
      ColorScheme.daily_card_reward_timer_border,
      LayoutScaler.widthFromDesign( 6 ),
    );

    pill.setClickable( false );
    pill.setOpacityImmediate( 0 );

    const clock_size = LayoutScaler.heightFromDesign( 58 );
    const clock_x = LayoutScaler.xFromDesign( 8 );
    const clock_y = ( h - clock_size ) / 2;

    const clock = new UIImage( this.ctx, clock_x, clock_y, clock_size, clock_size, ImgID.UI_ClockFrame );
    clock.setClickable( false );
    pill.addChild( clock );

    const text_x = clock_size;
    const text_w = w - clock_size;

    this.refresh_text = new UITextElement( this.ctx, text_x, 0, text_w, h, {
      text: "",
      font_size: LayoutScaler.textSizeFromDesign( 40 ),
      font_weight: 900,
      text_style: TextStyleScheme.rubik,
      text_color: ColorScheme.white,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_color: ColorScheme.hearts_text,
      auto_wrap: false,
    } );

    this.refresh_text.setClickable( false );
    pill.addChild( this.refresh_text );

    this.refresh_pill = pill;
    this.addChild( pill );
  }

  private rewardPopupSize(): { w: number; h: number }
  {
    return { w: LayoutScaler.widthFromDesign( 320 ), h: LayoutScaler.heightFromDesign( 200 ) };
  }

  private createRewardPopup(): void
  {
    const { w, h } = this.rewardPopupSize();

    const popup = new UIElementHolder( this.ctx, 0, 0, w, h, false );
    popup.setClickable( false );
    popup.setOpacityImmediate( 0 );

    const coin_size = LayoutScaler.widthFromDesign( REWARD_COIN_SIZE );
    const coin_x = ( w - coin_size ) / 2;

    const coin = new UIImage( this.ctx, coin_x, 0, coin_size, coin_size, ImgID.UI_ShopBlockImg );
    coin.setClickable( false );
    popup.addChild( coin );
    this.reward_icon = coin;

    const text_y = LayoutScaler.heightFromDesign( 130 );
    const text_h = LayoutScaler.heightFromDesign( 70 );

    this.reward_text = new UITextElement(
      this.ctx,
      0,
      text_y,
      w,
      text_h,
      {
        text: "",
        font_size: LayoutScaler.textSizeFromDesign( 55 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        outline_color: UIWheelSegments.TEXT_OUTLINE,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      }
    );
    this.reward_text.setClickable( false );
    popup.addChild( this.reward_text );

    this.reward_popup = popup;
    this.addChild( popup );
  }

  private showRewardPopup( reward: DailyTaskRewardDto | null, start_time: number ): void
  {
    const popup = this.reward_popup;
    if ( !popup ) return;

    const grow_ms = 320;
    const center = this.wheelCenter();
    const { w, h } = this.rewardPopupSize();
    const final_pos = new Vector2( center.x - w / 2, center.y - LayoutScaler.heightFromDesign( 355 ) );

    const coin_size = LayoutScaler.widthFromDesign( REWARD_COIN_SIZE );
    const start_scale = UIWheelSegments.COIN_SIZE / REWARD_COIN_SIZE;
    const start_coin_center_y = center.y - LayoutScaler.heightFromDesign( UIWheelSegments.COIN_R );
    const start_popup_center_y = start_coin_center_y + ( h / 2 - coin_size / 2 ) * start_scale;
    const start_pos = new Vector2( final_pos.x, start_popup_center_y - h / 2 );

    this.setRewardIcon( reward, w, coin_size );
    this.reward_text?.setText( reward ? UIWheelSegments.rewardLabel( reward ) : "0" );

    popup.stopAllTransforms();
    popup.setPixelPos( start_pos, true );
    popup.setOpacityImmediate( 0 );
    popup.setScaleImmediate( new Vector2( start_scale, start_scale ) );

    const grow_end = start_time + grow_ms;
    const normal = new Vector2( 1, 1 );
    const overshoot = new Vector2( 1.12, 1.12 );

    popup.addTransform( new TransformOpacity( popup, true, 0, 1, start_time, 200, { easing: Easing.EaseOutQuad } ) );
    popup.addTransform( new TransformScale( popup, true, null, overshoot, start_time, grow_ms, { easing: Easing.EaseOutQuad } ) );
    popup.addTransform( new TransformScale( popup, true, overshoot, normal, grow_end, 180, { easing: Easing.EaseInOutQuad } ) );
    popup.addTransform( new TransformStraightMove( popup, true, start_pos, final_pos, start_time, grow_ms, { easing: Easing.EaseOutQuad } ) );
  }

  private setRewardIcon( reward: DailyTaskRewardDto | null, popup_w: number, box: number ): void
  {
    const icon = this.reward_icon;
    if ( !icon ) return;

    const icon_id = reward ? UIWheelSegments.rewardIcon( reward ) : ImgID.UI_ShopBlockImg;
    icon.setOpacityImmediate( icon_id !== null ? 1 : 0 );
    if ( icon_id === null ) return;

    icon.setImageId( icon_id );
    const { w, h } = UIWheelSegments.fitSize( ImagesHolder.getImage( icon_id ), box );
    const x = ( popup_w - w ) / 2;
    const y = ( box - h ) / 2;
    icon.setBounds( x, y, w, h );
  }

  private createClaimButton(): void
  {
    const w = LayoutScaler.widthFromDesign( 640 );
    const h = LayoutScaler.heightFromDesign( 256 );
    const x = this.rect.getW2() - w / 2;
    const y = this.yFromWheel( BUTTON_OFFSET_Y ) - h / 2;

    const button = new UIImage( this.ctx, x, y, w, h, ImgID.UI_GreenFrameBtn, {}, true );

    const text = new UITextElement( this.ctx, 0, 0, w, h, {
      localization_key: "CLAIM",
      font_size: LayoutScaler.textSizeFromDesign( 67 ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_color: ColorScheme.menu_button_outline,
      ...TextStyleScheme.buttonShadow(),
    } );

    button.addChild( text, true );

    button.setOnClick( () =>
    {
      button.setClickable( false );
      this.claim();
    } );

    button.setClickable( false );
    button.setOpacityImmediate( 0 );

    this.claim_button = button;
    this.addChild( button );
  }

  private showClaimButton( start_time: number ): void
  {
    const button = this.claim_button;
    if ( !button ) return;

    const appear_ms = 250;

    button.stopAllTransforms();
    button.setOpacityImmediate( 0 );
    button.setScaleImmediate( new Vector2( 0.6, 0.6 ) );
    const appear = new TransformOpacity( button, true, 0, 1, start_time, appear_ms, { easing: Easing.EaseOutQuad } );
    appear.setOnFinished( () =>
    {
      if ( this.is_result_shown )
        button.setClickable( true );
    } );
    button.addTransform( appear );
    button.addTransform( new TransformScale( button, true, null, new Vector2( 1, 1 ), start_time, appear_ms, { easing: Easing.EaseOutQuad } ) );
    button.setClickable( false );
  }

  private kickPointer(): void
  {
    if ( !this.pointer ) return;

    this.pointer.stopAllTransforms();
    this.pointer.addTransform( new TransformRotate(
      this.pointer, true, -0.35, 0, performance.now(), 220, { easing: Easing.EaseOutQuad }
    ) );
  }

  private startIdleLights( start_time: number ): void
  {
    for ( let i = 1; i < this.glows.length; i++ )
    {
      const glow = this.glows[i];
      if ( !glow ) continue;

      const offset = i % 2 === 0 ? 0 : IDLE_TWINKLE_MS / 2;

      glow.addTransform( new TransformOpacity(
        glow, true, 0, 0.45, start_time + offset, IDLE_TWINKLE_MS,
        { easing: Easing.EaseInOutQuad, ping_pong_behaviour: true, infinite_loop: true }
      ) );
    }
  }

  private startChaseLights( now: number ): void
  {
    for ( let i = 1; i < this.glows.length; i++ )
    {
      this.glows[i]?.stopAllTransforms();
      this.glows[i]?.setOpacityImmediate( 0 );
      this.bulbs[i]?.fadeToOpacity( 0.4, 200, now, Easing.EaseOutQuad );
    }
  }

  private flashLights( now: number ): void
  {
    const flash_count = 3;
    const flash_total_ms = 1000;
    const flash_ms = flash_total_ms / flash_count;

    for ( let i = 1; i < this.glows.length; i++ )
    {
      const glow = this.glows[i];
      if ( !glow ) continue;

      glow.stopAllTransforms();
      glow.setOpacityImmediate( 0 );

      for ( let k = 0; k < flash_count; k++ )
      {
        glow.addTransform( new TransformOpacity(
          glow, true, 0, 1, now + k * flash_ms, flash_ms,
          { easing: Easing.EaseInOutQuad, ping_pong_behaviour: true }
        ) );
      }

      this.bulbs[i]?.fadeToOpacity( 1, 200, now, Easing.EaseOutQuad );
    }

    this.startIdleLights( now + flash_total_ms );
  }

  private updateSpinEffects( dt: number ): void
  {
    if ( !this.is_spinning || !this.segments ) return;

    const chase_min_speed = 3;
    const chase_max_speed = 26;
    const chase_trail = 3;

    const angle = this.segments.getRotationImportant();

    if ( Math.floor( angle / SEGMENT_ANGLE ) !== Math.floor( this.last_angle / SEGMENT_ANGLE ) )
      this.kickPointer();

    const angular_speed = dt > 0 ? Math.abs( angle - this.last_angle ) / dt : 0;
    this.last_angle = angle;

    const speed_k = Math.min( 1, angular_speed / this.max_angular_speed );
    this.chase_head += dt / 1000 * ( chase_min_speed + ( chase_max_speed - chase_min_speed ) * speed_k );

    const count = this.glows.length;

    for ( let i = 1; i < count; i++ )
    {
      const d = ( ( this.chase_head - i ) % count + count ) % count;
      this.glows[i]?.setOpacityImmediate( d < chase_trail ? 1 - d / chase_trail : 0 );
    }
  }

  public getDimOpacity(): number
  {
    return 0.8;
  }

  public onTouchStart( p: Vector2, now: number ): boolean
  {
    super.onTouchStart( p, now );
    return true;
  }

  public update( now: number ): void
  {
    super.update( now );

    const dt = this.last_update === 0 ? 0 : Math.min( 100, now - this.last_update );
    this.last_update = now;

    this.updateSpinEffects( dt );
    this.updateRefreshTimer();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "WheelOfFortuneOverlay";
  }
}
